import cx from 'classnames';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import type { ReactNode } from 'react';
import { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import * as Yup from 'yup';

import { createInverterAuthentication } from '../../../api/Optimise';
import { AppContext } from '../../../context/AppContext';
import { OnboardingContext } from '../../../context/OnboardingContext';
import type { InverterAuthDetails } from '../../../types/optimise';
import ErrorLabel from '../../../ui/ErrorLabel';
import LoopSpinner from '../../../ui/LoopSpinner';
import InfoModal from '../../../ui/Modal/InfoModal';
import { min } from '../../../utils/breakpoints';
import ensureError from '../../../utils/ensureError';
import generateRandomString from '../../../utils/generateRandomString';

import type { ConnectionField } from './inverterManufacturers';

interface OnboardingContextValues {
  setProgressPercentage: (value: number) => void;
  setManufacturerDetails: (value: Record<string, string>) => void;
}

interface AppContextValues {
  userSub: string | null;
}

export interface BaseConnectionContainerProps {
  fields: ConnectionField[];
  logo?: string | null;
  exampleModalContent?: ReactNode | null;
  inverterManufacturer: string;
  mode?: 'onboarding' | 'edit';
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Shown as a "choose a different manufacturer" link when the manufacturer step was skipped. */
  onBack?: () => void;
}

type FormValues = Record<string, string>;

const borderStyles = css`
  border: 1px solid ${(props) => props.theme.colors.light};
  border-radius: 4px;
  padding: 20px 20px 0 20px;
`;

const StyledFormContainer = styled.div<{ logo?: string | null }>`
  margin-top: 2rem;
  text-align: left;
  ${({ logo }) => logo && borderStyles};

  /* Mask text input to look like a password field without using type="password",
     which triggers password manager autofill heuristics */
  .masked-input {
    -webkit-text-security: disc;
  }
`;

const StyledLogo = styled.img`
  margin: 0 auto;
  display: block;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: inherit;
  text-decoration: underline !important;
  color: ${(props) => props.theme.colors.primary};
  display: inline-block;
  font-size: 14px;

  ${min.sm} {
    font-size: 16px;
  }
`;

const BaseConnectionContainer = ({
  fields,
  logo = null,
  exampleModalContent = null,
  inverterManufacturer,
  mode = 'onboarding',
  onSuccess,
  onCancel,
  onBack,
}: BaseConnectionContainerProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [error, setError] = useState<Error | undefined>(undefined);
  const { setProgressPercentage, setManufacturerDetails } = useContext(
    OnboardingContext
  ) as unknown as OnboardingContextValues;
  const { userSub } = useContext(AppContext) as unknown as AppContextValues;
  const [showInfo, setShowInfo] = useState(false);
  const [infoContent, setInfoContent] = useState<ReactNode>(undefined);

  const validationSchema = Yup.object().shape(
    fields.reduce<Record<string, Yup.AnySchema>>((acc, field) => {
      acc[field.name] = field.validation || Yup.string().required(`${field.label} is required`);
      return acc;
    }, {})
  );

  const showInfoModal = (e: React.MouseEvent, info: ReactNode) => {
    e.stopPropagation();
    setInfoContent(info);
    setShowInfo(true);
  };

  const prepareValues = (values: FormValues): FormValues =>
    values.api_password ? values : { ...values, api_password: generateRandomString(20) };

  const onSubmit = async (values: FormValues) => {
    setError(undefined);
    if (mode === 'onboarding') {
      setManufacturerDetails(values);
    }
    try {
      const prepared = prepareValues(values);
      // Constructed field-by-field (rather than spreading `prepared`) so TS can verify api_username/
      // api_password are present, satisfying InverterAuthDetails without an unsafe type assertion.
      const authDetails: InverterAuthDetails = {
        api_username: prepared.api_username,
        api_password: prepared.api_password,
        api_name: inverterManufacturer.toUpperCase(),
      };
      await createInverterAuthentication(authDetails, userSub);
      if (onSuccess) {
        onSuccess();
      } else {
        void navigate('/onboarding/connected');
      }
    } catch (err) {
      setError(ensureError(err));
    }
  };

  useEffect(() => {
    if (mode === 'onboarding') {
      setProgressPercentage(90);
    }
  }, [mode, setProgressPercentage]);

  return (
    <>
      <h1 className="mb-3" id="inverter-connection-heading">
        {mode === 'edit' ? 'Reconnect to your' : 'Connect to your'} {inverterManufacturer} system
      </h1>
      <div>
        {mode === 'onboarding' && (
          <>
            <p>Finally, please provide the details that {theme.language.appName} needs to connect to your system.</p>
            <StyledButton
              onClick={(e) =>
                showInfoModal(
                  e,
                  <>
                    {' '}
                    These details enable {theme.language.appName} to talk to your battery and ensure it operates
                    optimally based on our forecasts for your home's generation and demand.
                  </>
                )
              }
            >
              Why does {theme.language.appName} need to know this?
            </StyledButton>
          </>
        )}
        <StyledFormContainer logo={logo}>
          {logo && <StyledLogo src={logo} alt="Logo" />}
          <Formik<FormValues>
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={fields.reduce<FormValues>((acc, field) => {
              acc[field.name] = '';
              return acc;
            }, {})}
            validateOnChange
          >
            {({ handleSubmit, isValid, isSubmitting }) => (
              <Form onSubmit={handleSubmit} autoComplete="off">
                {fields.map((field) => (
                  <Field key={field.name} name={field.name}>
                    {({ field: formikField, meta }: FieldProps<string, FormValues>) => (
                      <Form.Group controlId={field.name}>
                        <Form.Label>{field.label}</Form.Label>
                        <Form.Control
                          type="text"
                          {...formikField}
                          placeholder={field.placeholder || field.label}
                          className={cx({ error: !!meta.error }, { 'masked-input': field.type === 'password' })}
                          autoComplete="off"
                          data-1p-ignore
                          data-lpignore
                          data-form-type="other"
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>
                ))}

                {exampleModalContent && (
                  <p className={cx({ 'text-center': logo })}>
                    If you need help finding these details, please take a look at{' '}
                    <StyledButton type="button" onClick={(e) => showInfoModal(e, exampleModalContent)}>
                      this example
                    </StyledButton>
                    .
                  </p>
                )}

                {error && (
                  <ErrorLabel
                    label={`Something went wrong - please check your details and try again. (${error.message})`}
                  />
                )}

                {onBack && (
                  <p className="text-center">
                    <StyledButton type="button" onClick={onBack}>
                      Not your inverter manufacturer? Choose a different one
                    </StyledButton>
                  </p>
                )}

                <Form.Group>
                  {!isSubmitting ? (
                    onCancel ? (
                      <div className="d-flex flex-column">
                        <Button
                          variant="primary"
                          className="mb-2 w-100"
                          disabled={!isValid || isSubmitting}
                          type="submit"
                        >
                          {mode === 'edit' ? 'Confirm' : 'Continue'}
                        </Button>
                        <Button variant="light" type="button" className="w-100" onClick={onCancel}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button variant="primary" disabled={!isValid || isSubmitting} className="w-100" type="submit">
                        {mode === 'edit' ? 'Confirm' : 'Continue'}
                      </Button>
                    )
                  ) : (
                    <LoopSpinner />
                  )}
                </Form.Group>
              </Form>
            )}
          </Formik>
        </StyledFormContainer>
      </div>

      <InfoModal show={showInfo} onClose={() => setShowInfo(false)} infoContent={infoContent} />
    </>
  );
};

export default BaseConnectionContainer;
